import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import type { AnsweredQuestion } from '@syndiqia/agents/contracts';
import { errorMessage, useApi } from '../api';
import { useI18n } from '../i18n';
import { useRegistryMutation } from '../queries';
import { useSyndicate } from '../session';
import { Alert } from './ui';

/** Ask a question answered from the syndicate's documents, with sources; forward it to the board if needed. */
export function AskQuestion({ onOpenDocument }: { onOpenDocument: (documentId: string) => void }) {
  const { t } = useI18n();
  const api = useApi();
  const s = useSyndicate();
  const [question, setQuestion] = useState('');
  const history = useQuery({ queryKey: [s.tenantId, 'questions'], queryFn: () => api<AnsweredQuestion[]>(`${s.api}/agents/questions`) });
  const ask = useRegistryMutation<string, AnsweredQuestion>((a, base, q) => a(`${base.replace(/\/registry$/, '/agents')}/questions`, { method: 'POST', body: { question: q } }), ['questions']);
  const forward = useRegistryMutation<string, { requestId: string }>(
    (a, base, id) => a(`${base.replace(/\/registry$/, '/agents')}/questions/${id}/forward`, { method: 'POST' }),
    ['questions', 'comms'],
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (question.trim().length >= 5) ask.mutate(question.trim());
  };
  const shown = ask.data ?? null;

  return (
    <section className="panel ask-panel">
      <h2>✦ {t.ask.title}</h2>
      <form onSubmit={submit} className="ask-form">
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={t.ask.placeholder} maxLength={1000} />
        <button type="submit" className="button button-primary" disabled={ask.isPending || question.trim().length < 5}>
          {t.ask.ask}
        </button>
      </form>
      {ask.isPending && <p className="muted small loading">{t.ask.thinking}</p>}
      {ask.isError && <Alert>{errorMessage(ask.error, t)}</Alert>}
      {shown && <Answer q={shown} onOpenDocument={onOpenDocument} forward={() => forward.mutate(shown.id)} forwarded={forward.isSuccess || !!shown.forwardedRequestId} />}

      {!!history.data?.length && !shown && (
        <details className="ask-history">
          <summary className="small">{t.ask.previous}</summary>
          {history.data.slice(0, 5).map((q) => (
            <Answer key={q.id} q={q} onOpenDocument={onOpenDocument} forward={() => forward.mutate(q.id)} forwarded={!!q.forwardedRequestId} />
          ))}
        </details>
      )}
    </section>
  );
}

function Answer({ q, onOpenDocument, forward, forwarded }: { q: AnsweredQuestion; onOpenDocument: (id: string) => void; forward: () => void; forwarded: boolean }) {
  const { t } = useI18n();
  const s = useSyndicate();
  return (
    <div className="ask-answer">
      <p className="small muted">« {q.question} »</p>
      {!q.answered && <p className="small"><strong>{t.ask.notCovered}</strong></p>}
      <p className="message-body">{q.answer}</p>
      {q.citations.length > 0 && (
        <ul className="citations">
          {q.citations.map((c) => (
            <li key={`${c.documentId}-${c.pageNo}-${c.topicId ?? ''}`}>
              {c.topicId ? (
                <Link to={`${s.base}/help#${c.topicId}`} className="small">
                  {t.help.guide} — {c.title}
                </Link>
              ) : (
                <button type="button" className="link-button small" onClick={() => onOpenDocument(c.documentId)}>
                  {c.title}, p. {c.pageNo}
                </button>
              )}
              <span className="muted small"> — « {c.quote} »</span>
            </li>
          ))}
        </ul>
      )}
      <p className="muted small">{t.ask.automatic}</p>
      {s.can('requests.create') &&
        (forwarded ? (
          <p className="small">
            ✓ <Link to={`${s.base}/requests`}>{t.ask.forwarded}</Link>
          </p>
        ) : (
          <button type="button" className="link-button small" onClick={forward}>
            {t.ask.forward}
          </button>
        ))}
    </div>
  );
}
